import type { FC } from "react";
import { useNavigate } from "react-router-dom";

import { LoginModalView, useActiveLoginModalContext } from "@/src/contexts";
import { getHostName, hostName, removeHost, requestTrack } from "@Components/Modules/Helpers";
import { showMainLoading } from "@Components/Modules/MainLoading";
import { type ILinkV2, LinkV2 } from "@Layout/ButtonLink";
import { HomeLogin, HomeRegistration } from "@Routes/HomepageLinks";
import { AllStaticPages } from "@Routes/StaticLandingPageLinks";

export interface ITransitionLinkV2 extends ILinkV2 {
    trackingUrl?: string;
    isExternal?: boolean;
}

export const TransitionLinkV2: FC<ITransitionLinkV2> = ({
    trackingUrl,
    telemetryCustom,
    isExternal,
    ...props
}) => {
    const { setActiveLoginModal } = useActiveLoginModalContext();
    const navigate = useNavigate();
    const onClick = (event: React.MouseEvent<HTMLAnchorElement>): boolean | void => {
        let result: boolean | void;

        requestTrack(trackingUrl);

        if (event.button === 1) {
            return true;
        }

        if (props.onClick) {
            result = props.onClick(event);
        }

        if (result === false) {
            return false;
        }

        if (props.target === "_blank") {
            return;
        }

        if (props.href) {
            if (event.ctrlKey) {
                return undefined;
            }

            if (navigator.userAgent.toLocaleUpperCase().indexOf("Goodie".toLocaleUpperCase()) > -1) {
                return true;
            }

            const destinationUrl = new URL(props.href, window.location.href);

            switch (destinationUrl.pathname) {
                case HomeRegistration:
                    setActiveLoginModal(LoginModalView.Registration);
                    event.stopPropagation();
                    return false;
                case HomeLogin:
                    setActiveLoginModal(LoginModalView.Login);
                    event.stopPropagation();
                    return false;
                default: {
                    const localDomain = hostName;

                    const destinationDomain = getHostName(props.href);

                    if (destinationDomain && localDomain.localeCompare(destinationDomain) !== 0) {
                        return result;
                    }

                    if (AllStaticPages.find((link: string) => link === removeHost(props.href || ""))) {
                        return result;
                    }
                    break;
                }
            }

            if (isExternal) {
                showMainLoading(null, event);
                window.history.scrollRestoration = "manual";
                return true;
            }

            event.preventDefault();
            event.stopPropagation();

            navigate(removeHost(props.href));
        }

        return result;
    };

    return <LinkV2 {...props} telemetryCustom={telemetryCustom} onClick={onClick} />;
};
